import { useState, useEffect } from 'react';
import { AlertCircle, TrendingDown, Percent } from 'lucide-react';
import api from '../utils/axios.ts';
import { toast } from 'react-hot-toast';

const LoanRestructureForm = ({ loan, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    restructure_type: 'extend_duration',
    new_principal: '',
    new_interest_rate: '',
    new_duration: '',
    new_start_date: '',
    reason: '',
    notes: '',
    restructure_date: new Date().toISOString().split('T')[0],
  });
  const [loading, setLoading] = useState(false);
  const [calculation, setCalculation] = useState(null);
  const [calculating, setCalculating] = useState(false);

  const restructureTypes = [
    { value: 'extend_duration', label: 'Extend Duration', description: 'Longer repayment period, lower installments' },
    { value: 'reduce_rate', label: 'Reduce Rate', description: 'Lower interest rate' },
    { value: 'reduce_principal', label: 'Reduce Principal', description: 'Forgive part of principal' },
    { value: 'payment_holiday', label: 'Payment Holiday', description: 'Pause payments temporarily' },
    { value: 'full_restructure', label: 'Full Restructure', description: 'Change multiple terms' },
  ];

  useEffect(() => {
    if (shouldCalculate()) {
      calculateRestructure();
    }
  }, [formData.new_principal, formData.new_interest_rate, formData.new_duration]);

  const shouldCalculate = () => {
    const { restructure_type, new_principal, new_interest_rate, new_duration } = formData;
    
    if (restructure_type === 'extend_duration' && new_duration) return true;
    if (restructure_type === 'reduce_rate' && new_interest_rate) return true;
    if (restructure_type === 'reduce_principal' && new_principal) return true;
    if (restructure_type === 'full_restructure' && (new_principal || new_interest_rate || new_duration)) return true;
    
    return false;
  };

  const calculateRestructure = async () => {
    setCalculating(true);
    try {
      const response = await api.post('/loan-restructures/calculate', {
        loan_id: loan.id,
        restructure_type: formData.restructure_type,
        new_principal: formData.new_principal || undefined,
        new_interest_rate: formData.new_interest_rate || undefined,
        new_duration: formData.new_duration || undefined,
      });
      setCalculation(response.data.data);
    } catch (error) {
      console.error('Calculation error:', error);
    } finally {
      setCalculating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    try {
      await api.post('/loan-restructures', {
        loan_id: loan.id,
        ...formData,
      });
      
      toast.success('Restructure request created successfully');
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create restructure request');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const selectedType = restructureTypes.find(t => t.value === formData.restructure_type);

  return (
    <div className="bg-white rounded-lg p-6">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Restructure Loan</h3>
        <p className="text-sm text-gray-600">
          Modify loan terms to help the borrower. This action requires approval.
        </p>
      </div>

      {/* Loan Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-blue-600">Loan Number</p>
            <p className="font-semibold text-blue-900">{loan.loan_no}</p>
          </div>
          <div>
            <p className="text-blue-600">Member</p>
            <p className="font-semibold text-blue-900">{loan.member?.name}</p>
          </div>
          <div>
            <p className="text-blue-600">Current Balance</p>
            <p className="font-semibold text-blue-900">{formatCurrency(loan.loan_balance)}</p>
          </div>
          <div>
            <p className="text-blue-600">Interest Rate</p>
            <p className="font-semibold text-blue-900">{loan.interest_rate}%</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Restructure Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Restructure Type *
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {restructureTypes.map((type) => (
              <button
                key={type.value}
                type="button"
                onClick={() => setFormData({ ...formData, restructure_type: type.value })}
                className={`p-4 border-2 rounded-lg text-left transition-colors ${
                  formData.restructure_type === type.value
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="font-semibold text-gray-900">{type.label}</div>
                <div className="text-sm text-gray-600 mt-1">{type.description}</div>
              </button>
            ))}
          </div>
        </div>

        {/* New Terms Based on Type */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(formData.restructure_type === 'reduce_principal' || formData.restructure_type === 'full_restructure') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Principal Amount
              </label>
              <input
                type="number"
                value={formData.new_principal}
                onChange={(e) => setFormData({ ...formData, new_principal: e.target.value })}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder={loan.principal}
                step="0.01"
                min="0"
              />
            </div>
          )}

          {(formData.restructure_type === 'reduce_rate' || formData.restructure_type === 'full_restructure') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Interest Rate (%)
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={formData.new_interest_rate}
                  onChange={(e) => setFormData({ ...formData, new_interest_rate: e.target.value })}
                  className="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder={loan.interest_rate}
                  step="0.01"
                  min="0"
                  max="100"
                />
                <Percent className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
            </div>
          )}

          {(formData.restructure_type === 'extend_duration' || formData.restructure_type === 'full_restructure') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Duration (months)
              </label>
              <input
                type="number"
                value={formData.new_duration}
                onChange={(e) => setFormData({ ...formData, new_duration: e.target.value })}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder={loan.duration}
                min="1"
              />
            </div>
          )}

          {formData.restructure_type === 'payment_holiday' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Holiday Duration (months)
              </label>
              <input
                type="number"
                value={formData.new_duration}
                onChange={(e) => setFormData({ ...formData, new_duration: e.target.value })}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="3"
                min="1"
              />
            </div>
          )}
        </div>

        {/* Calculation Results */}
        {calculation && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-semibold text-green-900 mb-3 flex items-center">
              <TrendingDown className="w-5 h-5 mr-2" />
              Restructure Impact
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-green-700">Current Installment</p>
                <p className="font-semibold text-green-900">{formatCurrency(calculation.original.installment)}</p>
              </div>
              <div>
                <p className="text-green-700">New Installment</p>
                <p className="font-semibold text-green-900">{formatCurrency(calculation.new.installment)}</p>
              </div>
              <div>
                <p className="text-green-700">Savings per Payment</p>
                <p className="font-semibold text-green-900">{formatCurrency(calculation.savings.installment_reduction)}</p>
              </div>
              <div>
                <p className="text-green-700">Reduction</p>
                <p className="font-semibold text-green-900">{calculation.savings.percentage.toFixed(1)}%</p>
              </div>
            </div>
          </div>
        )}

        {/* Reason */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Reason for Restructure *
          </label>
          <textarea
            value={formData.reason}
            onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
            className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            rows="3"
            placeholder="Explain why this restructure is needed..."
            required
          />
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Additional Notes
          </label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            rows="2"
            placeholder="Any additional details..."
          />
        </div>

        {/* Restructure Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Restructure Date *
          </label>
          <input
            type="date"
            value={formData.restructure_date}
            onChange={(e) => setFormData({ ...formData, restructure_date: e.target.value })}
            className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        {/* Warning */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-3" />
            <div className="flex-1">
              <p className="text-sm font-medium text-yellow-900">Important</p>
              <p className="text-sm text-yellow-800 mt-1">
                This restructure request will be sent for approval. Once approved, the loan schedule will be regenerated with the new terms.
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex space-x-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            disabled={loading || calculating}
          >
            {loading ? 'Creating...' : 'Create Restructure Request'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LoanRestructureForm;
